import { BodyData, ResponseData } from "@api/internal/utils/types";
import {
  getKeywordsToDisplayItems,
  getUrlPathFromAbsoluteUrl,
} from "@pages/Article/utils/helpers";
import { FeedArticleData, FeedArticleItems } from "@pages/Article/utils/types";
import {
  FeedTag,
  FeedTagData,
  FeedTagResponseData,
} from "@pages/Keyword/utils/types";
import {
  FeedSectionData,
  FeedSectionResponseData,
} from "@pages/Section/utils/types";
import { getEnvironment } from "@util/constant";
import { getEnvironmentDomain } from "@util/helpers";
import axios from "axios";

type UseFetchArticleProps = {
  /** Urlpath of the article to fetch the article */
  urlPath?: string;
  lang?: string;
};

/**
 * Handler to fetch section or tag data for the article's "Read More" section.
 */
export const fetchSectionTagData = async (
  body: Omit<BodyData, "btApiKey">,
  isDisplayTagData: FeedTag | undefined
): Promise<FeedSectionData | FeedTagData | undefined> => {
  if (isDisplayTagData) {
    const response = await axios.post<FeedTagResponseData>(
      "/_plat/api/v1/articles/tags",
      body
    );

    if (!response.data.success) return;

    return response.data.data;
  }

  const defaultSectionResponse = await axios.post<FeedSectionResponseData>(
    "/_plat/api/v1/articles/sections",
    body
  );

  if (!defaultSectionResponse.data.success) return;

  return defaultSectionResponse.data.data;
};

/**
 * Hook to fetch article data based on the provided URL path.
 */
export const useFetchArticle = async ({
  urlPath,
  lang = "en",
}: UseFetchArticleProps): Promise<FeedArticleData | string> => {
  const response = await axios.get<ResponseData<FeedArticleItems>>(
    "/_plat/api/v1/article",
    { params: { urlPath, lang } }
  );

  const responseData = response.data;

  if (!responseData.success) return responseData.error.message;

  const articleContent = responseData.data;

  const longURL = articleContent.urlPath;
  const path = getUrlPathFromAbsoluteUrl(longURL);
  const displayKeywordsReadmore = getKeywordsToDisplayItems(
    articleContent?.tags || []
  );

  const env = getEnvironment();
  const payload = {
    ...articleContent,
    urlPath:
      lang === "zh-hans"
        ? `${getEnvironmentDomain(env)}/zh-hans${path}`
        : longURL,
  };

  const keywordBody = {
    urlPath: displayKeywordsReadmore?.urlPath || "",
    size: 10,
  };

  const section = articleContent.sections?.[0];

  const sectionBody = {
    sections: [section?.uniqueName],
    size: 10,
    sortBy: "updated:desc",
    page: 1,
  };

  const readMoreSectionOrTagD = await fetchSectionTagData(
    displayKeywordsReadmore ? keywordBody : sectionBody,
    displayKeywordsReadmore
  );

  const isSummaryAvailable = !!(
    articleContent.aiSummary && articleContent.aiSummary.trim().length > 0
  );

  return {
    ...payload,
    readMoreSectionOrTagData: readMoreSectionOrTagD,
    isSummaryAvailable,
  };
};
